import { Button, Form, FormProps, Input, Popover } from 'antd';
import dayjs from 'dayjs';
import { FunctionComponent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTimer } from 'react-timer-hook';

import NotificationService from 'core/services/notification';
import styles from './otp.module.scss';
import Recaptcha from 'components/recaptcha/recaptcha';

interface OTPProps {
	ttl: number;
	isLoading: boolean;
	isResendLoading?: boolean;
	onConfirm: (otp: string) => void;
	onResend: (token: string) => void;
	onChange?: (value: string[]) => void;
	isInvalid?: boolean;
	style?: React.CSSProperties;
}

const OTP: FunctionComponent<OTPProps> = ({ ttl, onConfirm, onResend, onChange, isLoading, isResendLoading, isInvalid, style }) => {
	const { t } = useTranslation();
	const { start, minutes, seconds, totalSeconds, restart } = useTimer({
		expiryTimestamp: dayjs().add(ttl, 'second').toDate(),
		autoStart: false,
	});

	const [otpForm] = Form.useForm();

	type otpFieldType = {
		otp: string;
	};

	useEffect(() => {
		start();
	}, []);

	const onOtpFinish: FormProps<otpFieldType>['onFinish'] = async (values) => {
		try {
			onConfirm(values.otp);
		} catch (error: any) {
			NotificationService.error('Ops !!', 'Error');
		}
	};

	const handleResend = (token: string | null) => {
		if (token) {
			restart(dayjs().add(ttl, 'second').toDate(), true);
			onResend(token);
		}
	};

	return (
		<Form form={otpForm} onFinish={onOtpFinish} style={style}>
			<Form.Item name="otp" style={{ maxWidth: 'unset', direction: 'ltr' }} className="mb-4" rules={[{ required: true, min: 4, max: 4 }]}>
				<Input.OTP length={4} dir="ltr" type="number" status={isInvalid ? 'error' : ''} onInput={onChange} />
			</Form.Item>

			{isInvalid && <p style={{ fontSize: '16px', fontWeight: 500, color: 'red' }}>{t('REGISTER.INVALID_OTP')}</p>}

			<Form.Item>
				<Button block type="primary" htmlType="submit" loading={isLoading}>
					{t('COMMON.CONFIRM')}
				</Button>
			</Form.Item>

			<div className={styles.resendDiv}>
				<Popover placement="bottom" trigger="click" content={<Recaptcha onSubmit={handleResend} />}>
					<Button color="primary" disabled={totalSeconds > 0 || isResendLoading} type="link" loading={isResendLoading}>
						{t('RESET_PASSWORD.RESEND_OTP')}
					</Button>
				</Popover>

				<div className={styles.timer}>
					<p>{minutes}</p>
					<p>:</p>
					<p>{seconds}</p>
				</div>
			</div>
		</Form>
	);
};

export default OTP;
